import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, Download, FileArchive, RotateCcw } from "lucide-react";
import { supabase } from "@/project/integrations/supabase/client";
import { Shell, PageHeader } from "@/project/components/Shell";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TablePager } from "@/project/components/TablePager";
import { inr, shortDate } from "@/project/lib/format";
import { orgsQuery } from "@/project/lib/queries";
import { toCsv, downloadBlob, downloadZip } from "@/project/lib/report-export";

export const Route = createFileRoute("/letters/")({
  head: () => ({
    meta: [
      { title: "Letter History · Assistance & Letter Desk" },
      {
        name: "description",
        content:
          "Complete audit trail of every recommendation letter generated for partner organizations, with filters and export.",
      },
      { property: "og:title", content: "Recommendation Letter History" },
      {
        property: "og:description",
        content:
          "Filter, export and audit every recommendation letter generated by the office.",
      },
    ],
  }),
  component: LettersPage,
});

function LettersPage() {
  const [q, setQ] = useState("");
  const [org, setOrg] = useState("all");
  const [validity, setValidity] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const { data: orgs } = useQuery(orgsQuery);

  const { data } = useQuery({
    queryKey: ["letters-all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("generated_letters")
        .select("*, applications(ref_no, applicant_name, ration_card_no)")
        .order("generated_at", { ascending: false })
        .limit(1000);
      if (error) {
        throw error;
      }
      return data;
    },
  });

  const today = new Date().toISOString().slice(0, 10);

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (data ?? []).filter(l => {
      if (org !== "all" && l.organization_id !== org) {
        return false;
      }
      if (from && l.letter_date < from) {
        return false;
      }
      if (to && l.letter_date > to) {
        return false;
      }
      const valid = (l.valid_until ?? "") >= today;
      if (validity === "valid" && !valid) {
        return false;
      }
      if (validity === "expired" && valid) {
        return false;
      }
      if (!term) {
        return true;
      }
      return [
        l.organization_name,
        l.applications?.ref_no,
        l.applications?.applicant_name,
        l.applications?.ration_card_no,
      ]
        .filter(Boolean)
        .some(v => String(v).toLowerCase().includes(term));
    });
  }, [data, q, org, validity, from, to, today]);

  useEffect(() => {
    setPage(1);
  }, [q, org, validity, from, to, pageSize]);

  const paged = useMemo(
    () => rows.slice((page - 1) * pageSize, page * pageSize),
    [rows, page, pageSize]
  );

  const totalAmount = rows.reduce(
    (s, l) => s + Number(l.requested_amount ?? 0),
    0
  );

  const csv = () =>
    toCsv(
      [
        "Ref No",
        "Applicant",
        "Ration Card",
        "Organization",
        "Requested Amount",
        "Letter Date",
        "Letter Created",
        "Letter Generated",
        "Valid Until",
        "Validity",
      ],
      rows.map(l => [
        l.applications?.ref_no,
        l.applications?.applicant_name,
        l.applications?.ration_card_no,
        l.organization_name,
        l.requested_amount,
        l.letter_date,
        l.created_date,
        l.generated_at?.slice(0, 10),
        l.valid_until,
        (l.valid_until ?? "") >= today ? "Valid" : "Expired",
      ])
    );

  const stamp = new Date().toISOString().slice(0, 10);

  const reset = () => {
    setQ("");
    setOrg("all");
    setValidity("all");
    setFrom("");
    setTo("");
  };

  return (
    <Shell>
      <PageHeader
        title="Letter History"
        subtitle="Every recommendation letter issued, with validity tracking, filters and export"
        actions={
          <>
            <Button
              variant="outline"
              disabled={!rows.length}
              onClick={() =>
                downloadBlob(
                  new Blob([csv()], { type: "text/csv;charset=utf-8" }),
                  `letters-${stamp}.csv`
                )
              }
            >
              <Download className="mr-2 h-4 w-4" /> Export CSV
            </Button>
            <Button
              variant="outline"
              disabled={!rows.length}
              onClick={() =>
                downloadZip(
                  {
                    "README.txt": [
                      "Motisha Sadharmik Bhakti Kendra — Letter history export",
                      `Generated: ${new Date().toLocaleString("en-IN")}`,
                      `Letters: ${rows.length}`,
                      `Total recommended: ${totalAmount}`,
                    ].join("\n"),
                    "letters.csv": csv(),
                  },
                  `letters-${stamp}.zip`
                )
              }
            >
              <FileArchive className="mr-2 h-4 w-4" /> Share ZIP
            </Button>
          </>
        }
      />

      <Card className="mb-4">
        <CardContent className="grid gap-3 p-4 md:grid-cols-3 xl:grid-cols-6">
          <div className="md:col-span-3 xl:col-span-2">
            <Label className="text-xs text-muted-foreground">Search</Label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Organization, ref no, applicant, ration card…"
                value={q}
                onChange={e => setQ(e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Institution</Label>
            <Select value={org} onValueChange={setOrg}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Institution" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All institutions</SelectItem>
                {(orgs ?? []).map(o => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.short_name || o.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Validity</Label>
            <Select value={validity} onValueChange={setValidity}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All letters</SelectItem>
                <SelectItem value="valid">Valid only</SelectItem>
                <SelectItem value="expired">Expired only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">From date</Label>
            <Input
              type="date"
              className="mt-1"
              value={from}
              onChange={e => setFrom(e.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">To date</Label>
            <Input
              type="date"
              className="mt-1"
              value={to}
              onChange={e => setTo(e.target.value)}
            />
          </div>
          <div className="flex items-end">
            <Button variant="ghost" onClick={reset} className="w-full">
              <RotateCcw className="mr-2 h-4 w-4" /> Reset filters
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="data-table w-full text-sm">
              <thead className="bg-muted/60 text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Ref</th>
                  <th className="px-4 py-3">Applicant</th>
                  <th className="px-4 py-3">Organization</th>
                  <th className="px-4 py-3 text-right">Amount</th>
                  <th className="px-4 py-3">Letter Date</th>
                  <th className="px-4 py-3">Letter Created</th>
                  <th className="px-4 py-3">Letter Generated</th>
                  <th className="px-4 py-3">Valid Until</th>
                  <th className="px-4 py-3">Validity</th>
                  <th className="px-4 py-3">App</th>
                </tr>
              </thead>
              <tbody>
                {paged.map(l => (
                  <tr
                    key={l.id}
                    className="hover:bg-muted/40 border-t border-border"
                  >
                    <td className="px-4 py-2 font-medium">
                      <Link
                        to="/letters/$id"
                        params={{ id: l.id }}
                        className="underline"
                      >
                        {l.applications?.ref_no ?? "—"}
                      </Link>
                    </td>
                    <td className="px-4 py-2">
                      {l.applications?.applicant_name ?? "—"}
                    </td>
                    <td className="px-4 py-2">{l.organization_name}</td>
                    <td className="px-4 py-2 text-right">
                      {inr(l.requested_amount)}
                    </td>
                    <td className="px-4 py-2">{shortDate(l.letter_date)}</td>
                    <td className="px-4 py-2">{shortDate(l.created_date)}</td>
                    <td className="px-4 py-2">
                      {shortDate(l.generated_at?.slice(0, 10))}
                    </td>
                    <td className="px-4 py-2">{shortDate(l.valid_until)}</td>
                    <td className="px-4 py-2">
                      <Badge
                        variant={
                          (l.valid_until ?? "") >= today
                            ? "secondary"
                            : "outline"
                        }
                      >
                        {(l.valid_until ?? "") >= today ? "Valid" : "Expired"}
                      </Badge>
                    </td>
                    <td className="px-4 py-2">
                      <Link
                        to="/applications/$id"
                        params={{ id: l.application_id }}
                        className="text-xs underline"
                      >
                        App
                      </Link>
                    </td>
                  </tr>
                ))}
                {!paged.length && (
                  <tr>
                    <td
                      colSpan={10}
                      className="px-4 py-8 text-center text-muted-foreground"
                    >
                      No letters match your filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <TablePager
            total={rows.length}
            page={page}
            pageSize={pageSize}
            onPage={setPage}
            onPageSize={setPageSize}
          />
        </CardContent>
      </Card>
    </Shell>
  );
}
